import React, { FC, useState, useEffect, useCallback, useRef } from 'react';
import { ref, set, onValue, Unsubscribe } from 'firebase/database';
import { database } from '../../lib/firebase';
import { MapContainer, TileLayer, Circle, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { FEATURES } from '../../constants/featureFlags';

// Fix default marker icon
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

interface GeofenceSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  seniorId: string;
  seniorName: string;
  /** Current phone location for initial map center */
  currentLocation?: { lat: number; lng: number };
}

interface GeofenceConfig {
  enabled: boolean;
  homeLat: number;
  homeLng: number;
  radiusMeters: number;
  schedule: string;
}

const DISTANCE_PRESETS = [
  { label: 'Home', value: 100, description: '100 meters' },
  { label: 'Neighborhood', value: 500, description: '500 meters' },
  { label: 'Area', value: 1000, description: '1 kilometer' },
];

const SCHEDULE_PRESETS = [
  { label: 'Always (24/7)', value: '24/7', description: 'Monitor at all times' },
  { label: 'Nighttime', value: 'night', description: '5:00 PM - 9:00 AM' },
  { label: 'Daytime', value: 'day', description: '9:00 AM - 5:00 PM' },
];

// Click on map to set home location
const MapClickHandler: FC<{ onLocationSet: (lat: number, lng: number) => void }> = ({ onLocationSet }) => {
  useMapEvents({
    click: (e) => {
      onLocationSet(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

// Auto-center map when center changes
const MapCenterUpdater: FC<{ lat: number; lng: number; zoom?: number }> = ({ lat, lng, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (lat !== 0 && lng !== 0) {
      map.setView([lat, lng], zoom || map.getZoom(), { animate: true });
    }
  }, [lat, lng, zoom, map]);
  return null;
};

export const GeofenceSettingsModal: FC<GeofenceSettingsModalProps> = ({
  isOpen,
  onClose,
  seniorId,
  seniorName,
  currentLocation,
}) => {
  const [config, setConfig] = useState<GeofenceConfig>({
    enabled: false,
    homeLat: currentLocation?.lat || 0,
    homeLng: currentLocation?.lng || 0,
    radiusMeters: 500,
    schedule: '24/7',
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  // Stable reference for currentLocation to avoid re-fetching on every render
  const currentLocationRef = useRef(currentLocation);
  currentLocationRef.current = currentLocation;

  // Load existing config from Firebase.
  // Flag-gated for the same reason as GPSZoneDrawer's read: `devices/{id}/geofence`
  // is denied by the RTDB rules deployed 2026-08-16.
  useEffect(() => {
    if (!FEATURES.GEOFENCING_ENABLED) return;
    if (!isOpen || !seniorId) return;

    const configRef = ref(database, `devices/${seniorId}/geofence`);
    const unsub: Unsubscribe = onValue(configRef, (snapshot) => {
      const loc = currentLocationRef.current;
      if (snapshot.exists()) {
        const data = snapshot.val();
        setConfig({
          enabled: data.enabled ?? false,
          homeLat: data.homeLat ?? loc?.lat ?? 0,
          homeLng: data.homeLng ?? loc?.lng ?? 0,
          radiusMeters: data.radiusMeters ?? 500,
          schedule: data.schedule ?? '24/7',
        });
      } else if (loc) {
        // No config yet — default to current phone location as home
        setConfig(prev => ({
          ...prev,
          homeLat: loc.lat,
          homeLng: loc.lng,
        }));
      }
      setLoading(false);
    }, { onlyOnce: true });

    return () => unsub();
  }, [isOpen, seniorId]);

  const handleSave = useCallback(async () => {
    if (!seniorId) return;
    setSaving(true);
    setSaved(false);

    try {
      const configRef = ref(database, `devices/${seniorId}/geofence`);
      await set(configRef, {
        enabled: config.enabled,
        homeLat: config.homeLat,
        homeLng: config.homeLng,
        radiusMeters: config.radiusMeters,
        schedule: config.schedule,
        updatedAt: Math.floor(Date.now() / 1000),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error('Failed to save geofence config:', err);
    } finally {
      setSaving(false);
    }
  }, [seniorId, config]);

  // Geofencing is CUT from launch scope (Feature 03 spec: no geofence UI in
  // v1, postponed to Wave 1.5). Unlike GPSZoneDrawer there is nothing here to
  // preserve — every control in this modal, including its map, exists only to
  // configure the safe zone — so the whole modal is withheld rather than
  // partly hidden. The component is kept intact for the Wave 1.5 rebuild.
  //
  // This modal has no mount site today; the flag is the guarantee that it
  // cannot acquire one by accident before the Android flag flips.
  if (!FEATURES.GEOFENCING_ENABLED) return null;

  if (!isOpen) return null;

  const hasHomeLocation = config.homeLat !== 0 && config.homeLng !== 0;
  const mapCenter = hasHomeLocation
    ? { lat: config.homeLat, lng: config.homeLng }
    : currentLocation || { lat: 46.35, lng: -72.55 }; // Default: Trois-Rivieres, QC

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
              <span className="material-symbols-outlined text-emerald-600">fence</span>
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Safe Zone — {seniorName}</h2>
              <p className="text-sm text-gray-500">Set up geofencing to get alerts if they wander</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <span className="material-symbols-outlined text-gray-500">close</span>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Enable Toggle */}
          <div className="flex items-center justify-between p-4 sm:px-5 border-b border-gray-50">
            <div>
              <h3 className="text-base font-semibold text-gray-900">Enable Safe Zone</h3>
              <p className="text-sm text-gray-500">Get alerts when {seniorName} leaves the designated area</p>
            </div>
            <button
              onClick={() => setConfig(prev => ({ ...prev, enabled: !prev.enabled }))}
              className={`relative w-14 h-7 rounded-full transition-colors ${
                config.enabled ? 'bg-emerald-500' : 'bg-gray-300'
              }`}
            >
              <span className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${
                config.enabled ? 'translate-x-7' : ''
              }`} />
            </button>
          </div>

          {config.enabled && (
            <>
              {/* Map — Click to set home */}
              <div className="p-4 sm:px-5">
                <h3 className="text-sm font-semibold text-gray-700 mb-1">Home Location</h3>
                <div className="flex items-center gap-2 mb-3">
                  <p className="text-xs text-gray-400">Click on the map to set {seniorName}'s home location</p>
                  {currentLocation && (
                    <button
                      onClick={() => {
                        setConfig(prev => ({ ...prev, homeLat: currentLocation.lat, homeLng: currentLocation.lng }));
                      }}
                      className="shrink-0 flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors active:scale-[0.98]"
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>my_location</span>
                      Use Phone Location
                    </button>
                  )}
                </div>
                <div className="rounded-xl overflow-hidden border border-gray-200" style={{ height: '250px' }}>
                  {!loading && (
                    <MapContainer
                      center={[mapCenter.lat, mapCenter.lng]}
                      zoom={hasHomeLocation ? 15 : 12}
                      style={{ width: '100%', height: '100%' }}
                      scrollWheelZoom={true}
                    >
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      <MapClickHandler onLocationSet={(lat, lng) => {
                        setConfig(prev => ({ ...prev, homeLat: lat, homeLng: lng }));
                      }} />
                      <MapCenterUpdater lat={config.homeLat} lng={config.homeLng} />
                      {hasHomeLocation && (
                        <>
                          <Marker position={[config.homeLat, config.homeLng]} />
                          <Circle
                            center={[config.homeLat, config.homeLng]}
                            radius={config.radiusMeters}
                            pathOptions={{
                              color: '#10b981',
                              fillColor: '#10b981',
                              fillOpacity: 0.1,
                              weight: 2,
                            }}
                          />
                        </>
                      )}
                    </MapContainer>
                  )}
                </div>
                {!hasHomeLocation && (
                  <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                    <span className="material-symbols-outlined text-sm">warning</span>
                    Click on the map to set the home location
                  </p>
                )}
              </div>

              {/* Distance Presets */}
              <div className="px-4 sm:px-5 pb-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Safe Zone Distance</h3>
                <div className="grid grid-cols-3 gap-2">
                  {DISTANCE_PRESETS.map((preset) => (
                    <button
                      key={preset.value}
                      onClick={() => setConfig(prev => ({ ...prev, radiusMeters: preset.value }))}
                      className={`p-3 rounded-xl border-2 text-center transition-all ${
                        config.radiusMeters === preset.value
                          ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-semibold text-sm">{preset.label}</div>
                      <div className="text-xs mt-0.5 opacity-70">{preset.description}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Schedule Presets */}
              <div className="px-4 sm:px-5 pb-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Monitoring Schedule</h3>
                <div className="grid grid-cols-3 gap-2">
                  {SCHEDULE_PRESETS.map((preset) => (
                    <button
                      key={preset.value}
                      onClick={() => setConfig(prev => ({ ...prev, schedule: preset.value }))}
                      className={`p-3 rounded-xl border-2 text-center transition-all ${
                        config.schedule === preset.value
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-semibold text-sm">{preset.label}</div>
                      <div className="text-xs mt-0.5 opacity-70">{preset.description}</div>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 sm:p-5 border-t border-gray-100 bg-gray-50">
          <button onClick={onClose} className="px-5 py-2.5 text-gray-600 font-medium rounded-lg hover:bg-gray-100 transition-colors">
            Cancel
          </button>
          <div className="flex items-center gap-3">
            {saved && (
              <span className="text-emerald-600 text-sm font-medium flex items-center gap-1">
                <span className="material-symbols-outlined text-sm">check_circle</span>
                Saved!
              </span>
            )}
            <button
              onClick={handleSave}
              disabled={saving || (config.enabled && !hasHomeLocation)}
              className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98]"
            >
              {saving ? (
                <>
                  <span className="animate-spin material-symbols-outlined text-lg">progress_activity</span>
                  Saving...
                </>
              ) : (
                <>
                  <span className="material-symbols-outlined text-lg">save</span>
                  Save Settings
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeofenceSettingsModal;
